import React from 'react';
import {
  Box,
  Loader,
  Popover,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconCaretDownFilled } from '@tabler/icons-react';
import { usePortalProjectOptional } from '../../contexts/PortalProjectContext';
import { getProjectDisplayName } from '../../utils/projectDisplay';
import { isPortalDevReturnUrl } from '../../utils/cookies';

export function HeaderProjectSwitcher() {
  const portalProject = usePortalProjectOptional();
  const [opened, setOpened] = React.useState(false);

  const projectId = portalProject?.projectId ?? null;
  const projectName = portalProject?.projectName || getProjectDisplayName() || 'Project';
  const projectOptions = portalProject?.projectOptions ?? [];
  const canSwitchProject = portalProject?.canSwitchProject ?? false;
  const switching = portalProject?.switching ?? false;
  const switchProject = portalProject?.switchProject;
  const projectSelectEnabled = isPortalDevReturnUrl();

  const canOpen = canSwitchProject && projectSelectEnabled && projectOptions.length > 1;

  const selectContent = (
    <>
      <span className="header-project-switcher__label" title={projectName}>
        {projectName}
      </span>
      <span className="header-project-switcher__chevron" aria-hidden>
        {switching ? (
          <Loader size={12} color="#A3A9B5" />
        ) : (
          <IconCaretDownFilled size={14} />
        )}
      </span>
    </>
  );

  const readOnlySelect = (
    <Box
      className="header-project-switcher header-project-switcher--readonly"
      aria-label={`Current project: ${projectName}`}
    >
      {selectContent}
    </Box>
  );

  if (!canOpen) {
    return readOnlySelect;
  }

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      withArrow
      shadow="md"
      width={280}
    >
      <Popover.Target>
        <UnstyledButton
          className={`header-project-switcher${opened ? ' header-project-switcher--open' : ''}`}
          onClick={() => setOpened((o) => !o)}
          aria-label="Switch project"
          aria-expanded={opened}
          aria-haspopup="listbox"
          disabled={switching}
        >
          {selectContent}
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown p="xs">
        <Stack gap={4}>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={2}>
            Switch project
          </Text>
          {projectOptions.map((p) => {
            const isActive = p.id === projectId;
            return (
              <UnstyledButton
                key={p.id}
                className={`header-project-switcher__option${isActive ? ' header-project-switcher__option--active' : ''}`}
                onClick={() => {
                  if (!isActive && switchProject) {
                    void switchProject(p.id);
                  }
                  setOpened(false);
                }}
                disabled={switching}
              >
                <Text size="sm" fw={isActive ? 600 : 400} truncate style={{ minWidth: 0 }}>
                  {p.name || p.id}
                </Text>
              </UnstyledButton>
            );
          })}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
